import { convex } from "@/lib/convexClient";
import { CloudStorageClient } from "@/services/storage/CloudStorageClient";
import {
  BunnyStorageClient,
  isBunnyConfigured,
  getBunnyPullZoneHost,
} from "@/services/storage/BunnyStorageClient";
import { getProjectId, getCraftspaceId, StoragePaths } from "@/utils/storagePaths";
import { api } from "../../convex/_generated/api";

function getStorageClient() {
  return isBunnyConfigured() ? BunnyStorageClient : CloudStorageClient;
}

function isBunnyUrl(url: string): boolean {
  const host = getBunnyPullZoneHost();
  return (!!host && url.includes(host)) || url.includes(".b-cdn.net");
}

export class StorageService {
  /**
   * Upload a file to the private bucket
   */
  static async uploadPrivate(
    bucket: "staging",
    path: string,
    file: File | Blob,
    options?: { cacheControl?: string; upsert?: boolean },
  ) {
    const { data, error } = await getStorageClient().upload(bucket, path, file, options);

    if (error) {
      throw new Error(`Failed to upload to private bucket: ${error.message}`);
    }

    return { data, error: null };
  }

  /**
   * Upload a file to the public bucket
   */
  static async uploadPublic(
    bucket: "exports",
    path: string,
    file: File | Blob,
    options?: { cacheControl?: string; upsert?: boolean },
  ) {
    const { data, error } = await getStorageClient().upload(bucket, path, file, options);

    if (error) {
      throw new Error(`Failed to upload to public bucket: ${error.message}`);
    }

    return { data, error: null };
  }

  /**
   * Move a file from private bucket to public bucket.
   * For Bunny: no file copy needed (same path), just return the plain CDN URL.
   */
  static async moveToPublic(privatePath: string, publicPath: string) {
    if (isBunnyConfigured()) {
      const host = getBunnyPullZoneHost();
      const publicUrl = host
        ? `https://${host}/${publicPath.replace(/^\//, "")}`
        : publicPath;
      return { success: true, publicUrl };
    }

    const { data: privateData } = await StorageService.downloadFile("staging", privatePath);
    await StorageService.uploadPublic("exports", publicPath, privateData, { upsert: true });
    await StorageService.deleteFile("staging", privatePath);

    return { success: true };
  }

  /**
   * Get public URL for a file in the public bucket
   */
  static async getPublicUrl(bucket: "staging" | "exports", path: string) {
    const { data } = await getStorageClient().getPublicUrl(bucket, path);
    return data.publicUrl;
  }

  /**
   * Get signed URL for private access to a file
   */
  static async getPrivateUrl(
    bucket: "staging",
    path: string,
    expiresIn: number = 86400,
    craftspaceId?: string,
  ) {
    const { data, error } = await getStorageClient().createSignedUrl(
      bucket,
      path,
      expiresIn,
      craftspaceId,
    );

    if (error) {
      if (error.message.includes("Object not found") || error.message.includes("not found")) {
        throw new Error(`Object not found: ${path}`);
      }
      throw new Error(`Failed to create signed URL: ${error.message}`);
    }

    return { data, error: null };
  }

  /**
   * Get signed URL with automatic refresh if expired
   */
  static async getPrivateUrlWithRefresh(
    bucket: "staging",
    path: string,
    expiresIn: number = 86400,
    craftspaceId?: string,
  ) {
    try {
      const result = await StorageService.getPrivateUrl(bucket, path, expiresIn, craftspaceId);
      return result;
    } catch (error) {
      console.error("Error getting private URL:", error);
      throw error;
    }
  }

  /**
   * Check if a signed URL is still valid
   */
  static async isUrlValid(url: string): Promise<boolean> {
    // Skip problematic S3 URLs to prevent 403 errors
    if (url.includes("crafter.learn.360creators.com") && url.includes("thumbnail_")) {
      console.log("⏭️ Skipping known problematic S3 URL in isUrlValid:", url);
      return false;
    }

    try {
      const response = await fetch(url, { method: "HEAD" });
      return response.ok;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Refresh expired URLs for a panorama
   */
  static async refreshPanoramaUrls(panoramaId: string): Promise<{
    success: boolean;
    privateUrl?: string;
    thumbnailUrl?: string;
    error?: string;
  }> {
    try {
      const panorama = await convex.query(api.panoramas.get, { id: panoramaId });
      if (!panorama) {
        throw new Error("Failed to fetch panorama");
      }

      const updates: Record<string, unknown> = {};
      let needsUpdate = false;

      // Get craftspace ID
      let craftspaceId = "default";
      if (panorama.project_id) {
        const project = await convex.query(api.projects.get, { id: panorama.project_id });
        craftspaceId = project?.craftspace_id || "default";
      }

      // Helper function to refresh a single URL
      const refreshUrl = async (url: string): Promise<string | null> => {
        // Bunny CDN URL (token auth or legacy without token) — regenerate via Convex action
        if (isBunnyUrl(url)) {
          const urlObj = new URL(url);
          const path = urlObj.pathname.replace(/^\/+/, "");
          const {
            data: { signedUrl },
          } = await StorageService.getPrivateUrl("staging", path, 86400, craftspaceId);
          return signedUrl;
        }

        // Supabase/Convex signed URL
        const path = StorageService.extractPathFromUrl(url);
        if (path) {
          const {
            data: { signedUrl },
          } = await StorageService.getPrivateUrl("staging", path);
          return signedUrl;
        }

        // S3 URL
        const { StorageServiceFactory } = await import(
          "@/services/storage/StorageServiceFactory"
        );
        const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
        const urlObj = new URL(url);
        const pathname = urlObj.pathname.replace(/^\/+/, "");
        const segments = pathname.split("/").filter(Boolean);
        const filePath = segments.length > 1 ? segments.slice(1).join("/") : pathname;
        return await storageService.getFileUrl(filePath);
      };

      // Refresh private file URL
      if (panorama.private_file_url) {
        try {
          const refreshedUrl = await refreshUrl(panorama.private_file_url);
          if (refreshedUrl) {
            updates.private_file_url = refreshedUrl;
            needsUpdate = true;
          }
        } catch (error) {
          console.error("Failed to refresh private file URL:", error);
        }
      }

      // Refresh thumbnail URL
      if (panorama.thumbnail_url) {
        try {
          const refreshedUrl = await refreshUrl(panorama.thumbnail_url);
          if (refreshedUrl) {
            updates.thumbnail_url = refreshedUrl;
            needsUpdate = true;
          }
        } catch (error) {
          console.error("Failed to refresh thumbnail URL:", error);
        }
      }

      if (needsUpdate) {
        await convex.mutation(api.panoramas.update, { id: panoramaId, ...updates });
      }

      return {
        success: true,
        privateUrl: updates.private_file_url as string | undefined,
        thumbnailUrl: updates.thumbnail_url as string | undefined,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Extract file path from Supabase/Convex signed URL
   */
  private static extractPathFromUrl(url: string): string | null {
    try {
      if (url.includes("/storage/v1/object/sign/")) {
        const parts = url.split("/storage/v1/object/sign/");
        if (parts.length > 1) {
          return parts[1].split("?")[0].replace(/^staging\//, "");
        }
      }
      if (url.includes("/storage/v1/object/public/")) {
        const parts = url.split("/storage/v1/object/public/");
        if (parts.length > 1) {
          return parts[1].split("?")[0].replace(/^staging\//, "");
        }
      }
      return null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Delete a file from storage
   */
  static async deleteFile(bucket: "staging" | "exports", path: string) {
    const { error } = await getStorageClient().remove(bucket, [path]);

    if (error) {
      throw new Error(`Failed to delete file: ${error.message}`);
    }

    return { success: true };
  }

  /**
   * List files in a directory
   */
  static async listFiles(bucket: "staging" | "exports", path: string) {
    const { data, error } = await getStorageClient().list(bucket, path);

    if (error) {
      throw new Error(`Failed to list files: ${error.message}`);
    }

    return data;
  }

  /**
   * Remove multiple files from storage in a single call
   */
  static async removeFiles(bucket: "staging" | "exports", paths: string[]) {
    if (paths.length === 0) return { success: true };

    const { error } = await getStorageClient().remove(bucket, paths);

    if (error) {
      throw new Error(`Failed to remove files: ${error.message}`);
    }

    return { success: true };
  }

  /**
   * Download a file from storage
   */
  static async downloadFile(bucket: "staging" | "exports", path: string) {
    const { data, error } = await getStorageClient().download(bucket, path);

    if (error) {
      throw new Error(`Failed to download file: ${error.message}`);
    }

    return { data, error: null };
  }

  /**
   * Create a signed upload URL for direct uploads
   */
  static async createSignedUploadUrl(_bucket: "staging", path: string) {
    const { data, error } = await getStorageClient().createSignedUploadUrl(path);

    if (error) {
      throw new Error(`Failed to create signed upload URL: ${error.message}`);
    }

    return { data, error: null };
  }
}

/**
 * Helper functions for common storage operations
 */
export const StorageHelpers = {
  async uploadPanorama(userId: string, projectId: string, file: File) {
    const craftspaceId = getCraftspaceId(userId);
    const projectPath = getProjectId(projectId);
    const filename = `${Date.now()}_${file.name}`;
    const panoramaName = file.name
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-zA-Z0-9-_]/g, "_")
      .toLowerCase();
    const path = StoragePaths.private.panoramaOriginal(
      craftspaceId,
      projectPath,
      panoramaName,
      filename,
    );

    const result = await StorageService.uploadPrivate("staging", path, file);
    return result.data;
  },

  async uploadThumbnail(userId: string, projectId: string, file: File) {
    const craftspaceId = getCraftspaceId(userId);
    const projectPath = getProjectId(projectId);
    const filename = `${Date.now()}_${file.name}`;
    const panoramaName = file.name
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-zA-Z0-9-_]/g, "_")
      .toLowerCase();
    const path = StoragePaths.private.thumbnail(craftspaceId, projectPath, panoramaName, filename);

    return await StorageService.uploadPrivate("staging", path, file);
  },

  async publishPanorama(userId: string, projectId: string, privatePath: string, filename: string) {
    const craftspaceId = getCraftspaceId(userId);
    const projectPath = getProjectId(projectId);
    const publicPath = StoragePaths.public.panorama(craftspaceId, projectPath, filename);

    return await StorageService.moveToPublic(privatePath, publicPath);
  },

  async publishThumbnail(userId: string, projectId: string, privatePath: string, filename: string) {
    const craftspaceId = getCraftspaceId(userId);
    const projectPath = getProjectId(projectId);
    const publicPath = StoragePaths.public.thumbnail(craftspaceId, projectPath, filename);

    return await StorageService.moveToPublic(privatePath, publicPath);
  },
};
